export const WEFAT_PAGE_SCROLL_API_NAMES = Object.freeze(['pageScrollTo']);
export class WxPageScrollRuntimeError extends Error {
    api = 'pageScrollTo';
    code;
    errCode;
    errMsg;
    remediation;
    constructor(code, reason, remediation, cause) {
        const errMsg = `pageScrollTo:fail ${reason}`;
        super(`${code} ${errMsg}`);
        this.name = 'WxPageScrollRuntimeError';
        this.code = code;
        this.errCode = code;
        this.errMsg = errMsg;
        this.remediation = remediation;
        if (cause !== undefined)
            Object.defineProperty(this, 'cause', { value: cause });
    }
}
const CALLBACK_KEYS = new Set(['success', 'fail', 'complete']);
const OPTION_KEYS = new Set([
    ...CALLBACK_KEYS,
    'duration',
    'offsetTop',
    'scrollTop',
    'selector',
]);
function isPlainRecord(value) {
    if (value === null || typeof value !== 'object' || Array.isArray(value))
        return false;
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}
function optionError(reason) {
    return new WxPageScrollRuntimeError('WF6171', reason, 'Pass a finite scrollTop or a non-empty selector with optional finite offsetTop and non-negative duration.');
}
function driverError(error) {
    if (error instanceof WxPageScrollRuntimeError)
        return error;
    return new WxPageScrollRuntimeError('WF6170', error instanceof Error ? error.message : String(error), 'Call wx.pageScrollTo after the generated page viewport is mounted and target an element inside the active page.', error);
}
function readTarget(raw) {
    if (!isPlainRecord(raw))
        throw optionError('options must be a plain object');
    for (const key of Reflect.ownKeys(raw)) {
        const descriptor = typeof key === 'string' ? Object.getOwnPropertyDescriptor(raw, key) : undefined;
        if (typeof key !== 'string' || !OPTION_KEYS.has(key)) {
            throw optionError(`unsupported option field ${String(key)}`);
        }
        if (descriptor === undefined || !('value' in descriptor)) {
            throw optionError(`option field ${key} must be a direct data property`);
        }
        if (CALLBACK_KEYS.has(key) && descriptor.value !== undefined && typeof descriptor.value !== 'function') {
            throw optionError(`${key} must be a function when provided`);
        }
    }
    const duration = raw.duration ?? 300;
    if (typeof duration !== 'number' || !Number.isFinite(duration) || duration < 0) {
        throw optionError('duration must be a finite non-negative number');
    }
    if (raw.selector !== undefined) {
        if (typeof raw.selector !== 'string' || raw.selector.length === 0) {
            throw optionError('selector must be a non-empty string when provided');
        }
        const offsetTop = raw.offsetTop ?? 0;
        if (typeof offsetTop !== 'number' || !Number.isFinite(offsetTop)) {
            throw optionError('offsetTop must be a finite number when provided');
        }
        return {
            callbacks: raw,
            target: Object.freeze({ duration, kind: 'selector', offsetTop, selector: raw.selector }),
        };
    }
    if (raw.offsetTop !== undefined) {
        throw optionError('offsetTop requires selector');
    }
    if (typeof raw.scrollTop !== 'number' || !Number.isFinite(raw.scrollTop) || raw.scrollTop < 0) {
        throw optionError('scrollTop must be a finite non-negative number when selector is absent');
    }
    return {
        callbacks: raw,
        target: Object.freeze({ duration, kind: 'scrollTop', scrollTop: raw.scrollTop }),
    };
}
function invokeCallback(callback, value, kind, report) {
    if (callback === undefined)
        return;
    try {
        callback(value);
    }
    catch (error) {
        if (report !== undefined) {
            try {
                report(error, 'pageScrollTo', kind);
                return;
            }
            catch (reportError) {
                queueMicrotask(() => { throw reportError; });
                return;
            }
        }
        queueMicrotask(() => { throw error; });
    }
}
export function createPageScrollWxApis(options) {
    if (!isPlainRecord(options) || !isPlainRecord(options.driver)) {
        throw new TypeError('createPageScrollWxApis requires a plain driver object');
    }
    if (typeof options.driver.pageScrollTo !== 'function') {
        throw new TypeError('page scroll driver requires pageScrollTo');
    }
    if (options.onCallbackError !== undefined && typeof options.onCallbackError !== 'function') {
        throw new TypeError('onCallbackError must be a function when provided');
    }
    return Object.freeze({
        pageScrollTo(raw) {
            const { callbacks, target } = readTarget(raw);
            const task = Promise.resolve()
                .then(() => options.driver.pageScrollTo(target))
                .then(() => Object.freeze({ errMsg: 'pageScrollTo:ok' }));
            if (callbacks.success === undefined && callbacks.fail === undefined && callbacks.complete === undefined) {
                return task.catch((error) => { throw driverError(error); });
            }
            void task.then((result) => {
                invokeCallback(callbacks.success, result, 'success', options.onCallbackError);
                invokeCallback(callbacks.complete, result, 'complete', options.onCallbackError);
            }, (error) => {
                const failure = driverError(error);
                invokeCallback(callbacks.fail, failure, 'fail', options.onCallbackError);
                invokeCallback(callbacks.complete, failure, 'complete', options.onCallbackError);
            });
            return undefined;
        },
    });
}
//# sourceMappingURL=page-scroll-adapter.js.map